import React, { useCallback, useEffect, useRef } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE_PATHS } from '@constants';
import { useStore } from '@hooks';
import { Loader } from '@ui';
import { errorHandler } from '@utils';
import { StyledWrapper } from '../BigCommerce/Callback';

/**
 * `state` is round-tripped through the identity provider, so it can come back
 * absent or malformed. Never let JSON.parse throw inside the effect.
 *
 * @param {string|null} raw the raw `state` query parameter
 * @returns {Object|null} the parsed state, or null when it is unusable
 */
const parseState = (raw) => {
    try {
        return JSON.parse(raw);
        // eslint-disable-next-line no-unused-vars
    } catch (_error) {
        return null;
    }
};

const OauthIdentityCallback = () => {
    const store = useStore();
    const navigate = useNavigate();
    const { platformOauthCallback } = store.authStore;

    const [searchParams] = useSearchParams();
    // KLIC-11373: the API consumes the state nonce cookie on the first callback,
    // so a second POST of the same code would always fail. Run it once.
    const requested = useRef(false);
    // The request may resolve after the user has already left this route; its
    // navigate/toLogin continuation must not yank them back.
    const active = useRef(true);

    const toLogin = useCallback(
        (email, platform) =>
            navigate(
                email && platform
                    ? {
                          pathname: ROUTE_PATHS.LOGIN,
                          search: createSearchParams({ oauth_email: email, platform }).toString()
                      }
                    : ROUTE_PATHS.LOGIN
            ),
        [navigate]
    );

    useEffect(() => {
        active.current = true;

        if (requested.current) {
            return;
        }

        requested.current = true;

        (async () => {
            const state = parseState(searchParams.get('state'));

            if (!state?.platform) {
                return toLogin();
            }

            try {
                const response = await platformOauthCallback(state.platform, Object.fromEntries([...searchParams]));

                if (!active.current) {
                    return;
                }

                if (response?.success) {
                    return navigate(ROUTE_PATHS.HOME);
                }

                toLogin(response?.email, response?.platform);
            } catch (error) {
                if (!active.current) {
                    return;
                }

                errorHandler({ error });
                toLogin();
            }
        })();

        return () => {
            active.current = false;
        };
    }, [searchParams]);

    return (
        <StyledWrapper justify='center' align='center'>
            <Loader size='120px' />
        </StyledWrapper>
    );
};

export default OauthIdentityCallback;
